import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { styles } from "./styles";
import Box from "@mui/material/Box";
import Loader from "components/loader";
import Button from "@mui/material/Button";
import { useDispatch, useSelector } from "react-redux";
import AnimateButton from "components/animateButton";
import CustomModal from "components/customModal";
import { ReducersModal } from "utils/modals";
import Grid from "@mui/material/Grid";

import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import {
  UpdatePricing,
  getCategoryTypeOptions,
  getServicePricingForUpdate,
  getServicePricingListing,
} from "./action";
import {
  SERVICE_CATEGORY,
  SERVICE_CATEGORY_LABEL,
  hasCategoryTypeFilter,
  toServiceCategory,
} from "utils/serviceCategory";
import {
  getServicePriceUpdateId,
  getServicePricingRecordId,
} from "utils/apiMappers";
import moment from "moment";

const toNumericPrice = (value: any) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

export default function UpdatePriceModal({ state, setState }: any) {
  const { category, categoryTypeId, categoryTypes } = useSelector(
    (state: ReducersModal) => state.ServicePricingReducer
  );
  const dispatch: any = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [pricingList, setPricingList] = useState<any[]>([]);
  const [editedPrices, setEditedPrices] = useState<Record<string, string>>({});
  const [selectedTypeId, setSelectedTypeId] = useState("");

  const resolvedCategory = toServiceCategory(category);
  const isBike = resolvedCategory === SERVICE_CATEGORY.BIKE;
  const showTypeFilter = hasCategoryTypeFilter(resolvedCategory) && !isBike;
  const typeLabel = `${SERVICE_CATEGORY_LABEL[resolvedCategory]} Type`;

  const fetchPricing = (typeId?: string) => {
    setLoading(true);
        dispatch(
      getServicePricingForUpdate((cbData: any) => {
        const rows = cbData || [];
                setPricingList(rows);
        setEditedPrices({});
        setLoading(false);
      }, typeId)
    );
  };

  useEffect(() => {
    if (!state) return;
    if (showTypeFilter && !(categoryTypes || []).length) {
            dispatch(getCategoryTypeOptions());
    }
    const nextTypeId = showTypeFilter ? categoryTypeId || "" : "";
    setSelectedTypeId(nextTypeId);
        if (!showTypeFilter) {
      fetchPricing();
    } else {
      fetchPricing(nextTypeId || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const handleClose = () => {
    setState(false);
    setPricingList([]);
    setEditedPrices({});
    setSelectedTypeId("");
  };

  const getEditedPricings = () =>
    (pricingList || []).reduce(
      (acc: { id: string; price: number }[], item: any) => {
        const id = getServicePriceUpdateId(item);
        if (!id || !Object.prototype.hasOwnProperty.call(editedPrices, id)) {
          return acc;
        }
        const price = toNumericPrice(editedPrices[id]);
        const originalPrice = toNumericPrice(item?.price);
        if (!Number.isFinite(price) || price === originalPrice) {
          return acc;
        }
        acc.push({ id, price });
        return acc;
      },
      []
    );

  const handleOnSubmit = () => {
    const pricings = getEditedPricings();
        if (!pricings.length) {
      setLoading(false);
      return;
    }

    setLoading(true);
    dispatch(
      UpdatePricing(pricings, (response: any) => {
        setLoading(false);
        if (response?.isSuccess) {
          dispatch(getServicePricingListing());
          handleClose();
        }
      })
    );
  };

  const initialValues = {
    categoryTypeId: selectedTypeId,
  };

  return (
    <Formik
      enableReinitialize
      onSubmit={handleOnSubmit}
      initialValues={initialValues}
    >
      {({ handleSubmit }) => (
        <>
          <CustomModal
            visible={state}
            title="Update Service Pricing"
            onClose={handleClose}
            onSubmit={handleSubmit}
            maxWidth={900}
            footer={
              <AnimateButton>
                <Button
                  disabled={!getEditedPricings().length}
                  disableElevation
                  size="large"
                  type="submit"
                  variant="contained"
                  color="secondary"
                >
                  Update
                </Button>
              </AnimateButton>
            }
          >
            {showTypeFilter && (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel
                      shrink={Boolean(selectedTypeId)}
                      sx={!selectedTypeId ? { display: "none" } : undefined}
                    >
                      {typeLabel}
                    </InputLabel>
                    <Select
                      sx={styles.selectDropdown}
                      label={typeLabel}
                      name="categoryTypeId"
                      native={false}
                      variant="outlined"
                      displayEmpty
                      notched={Boolean(selectedTypeId)}
                      value={selectedTypeId}
                      renderValue={(selected) => {
                        const selectedId = String(selected || "");
                        if (!selectedId) return typeLabel;
                        const match = (categoryTypes || []).find(
                          (item: any) =>
                            String(item._id || item.id) === selectedId
                        );
                        return match?.label || typeLabel;
                      }}
                      onChange={(e: any) => {
                        const nextId = String(e.target.value || "");
                        setSelectedTypeId(nextId);
                        fetchPricing(nextId || undefined);
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      {(categoryTypes || []).map((item: any) => (
                        <MenuItem
                          key={item._id || item.id}
                          value={item._id || item.id}
                        >
                          {item.label || "N/A"}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
            <Box sx={styles.paper}>
              <TableContainer sx={{ maxHeight: "55vh" }}>
                <Table sx={styles.table}>
                  <TableHead sx={{ ...styles.tableHeader }}>
                    <TableRow sx={styles.headerRow}>
                      <TableCell>{"Service Type"}</TableCell>
                      <TableCell>{"Service Duration"}</TableCell>
                      <TableCell>{"Created At"}</TableCell>
                      <TableCell>{"Price"}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ marginBottom: "0px !important" }}>
                    {pricingList && pricingList.length > 0 ? (
                      pricingList.map((val: any) => {
                        const rowId = getServicePricingRecordId(val);
                        const priceId = getServicePriceUpdateId(val);
                        const canEditPrice = Boolean(priceId);
                        return (
                          <TableRow key={rowId} sx={{ ...styles.row }}>
                            <TableCell>
                              {typeof val?.serviceType === "string"
                                ? val.serviceType
                                : val?.serviceType?.serviceType || ""}
                            </TableCell>
                            <TableCell>
                              {val?.serviceDuration ||
                                val?.serviceType?.serviceTime ||
                                ""}
                            </TableCell>
                            <TableCell sx={{ textTransform: "capitalize" }}>
                              {val?.createdOn || val?.createdAt
                                ? moment(
                                    val.createdOn || val.createdAt
                                  ).format("DD/MM/YYYY")
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              <TextField
                                sx={{ width: "150px !important" }}
                                variant="outlined"
                                type="text"
                                disabled={!canEditPrice}
                                value={
                                  canEditPrice
                                    ? editedPrices[priceId] ??
                                      val?.price ??
                                      ""
                                    : val?.price ?? ""
                                }
                                onChange={(e) => {
                                  if (!canEditPrice) return;
                                  setEditedPrices({
                                    ...editedPrices,
                                    [priceId]: e.target.value,
                                  });
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={15} sx={styles.noDataCell}>
                          <Box>{"No Data Found"}</Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </CustomModal>
          {loading && <Loader />}
        </>
      )}
    </Formik>
  );
}
